/* Centred add-to-cart confirmation.

   This registers the `cart-notification` custom element. That name is not a
   choice: Dawn's product-form.js does

     this.cart = document.querySelector('cart-notification') || ...

   and when it finds nothing it abandons the AJAX add entirely and sends the
   browser to /cart. So the element has to exist and has to answer the small
   interface product-form.js calls into: getSectionsToRender(), setActiveElement()
   and renderContents().

   Because a custom element name can only be defined once, minery-header.liquid
   deliberately does not load Dawn's cart-notification.js. Whichever script ran
   first would win, so loading both would be a race.

   The dialog opens from renderContents() and nowhere else, which is what keeps
   it to "only when the shopper adds something to the cart". */

(function () {
  'use strict';

  if (customElements.get('cart-notification')) return;

  // Focus handed back after a tap keeps the screen reader in place, but
  // Safari on iPhone drew a ring round the button for it. .m-nofocusring
  // (minery.css) hides that ring until the button next loses focus.
  // The last thing the shopper used: a pointer (mouse, finger, pen) or the
  // keyboard. A dialog opened by a pointer takes focus itself rather than
  // letting showModal() ring its close button as though already chosen.
  var byKeyboard = false;
  document.addEventListener('keydown', function (e) {
    if (!e.metaKey && !e.ctrlKey && !e.altKey) byKeyboard = true;
  }, true);
  document.addEventListener('pointerdown', function () {
    byKeyboard = false;
  }, true);

  function quietOpenFocus(dialog) {
    if (byKeyboard) return;
    dialog.setAttribute('tabindex', '-1');
    dialog.focus({ preventScroll: true });
  }

  function quietFocus(el) {
    if (window.matchMedia && window.matchMedia('(hover: none), (pointer: coarse)').matches) {
      el.classList.add('m-nofocusring');
      el.addEventListener('blur', function drop() {
        el.classList.remove('m-nofocusring');
        el.removeEventListener('blur', drop);
      });
    }
    el.focus({ preventScroll: true });
  }

  function nodeFrom(html, selector) {
    if (!html) return null;
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  class MineryCartPopup extends HTMLElement {
    constructor() {
      super();

      this.dialog = this.querySelector('[data-m-cadd-dialog]');
      this.body = this.querySelector('[data-m-cadd-body]');
      this.activeElement = null;
      this.recsFor = null;

      // Delegated, because everything inside .m-cadd__body is replaced on
      // every add and re-bound listeners would leak.
      this.addEventListener('click', function (e) {
        if (e.target.closest('[data-m-cadd-close]')) {
          e.preventDefault();
          this.close();
        }
      }.bind(this));

      this.addEventListener('change', function (e) {
        if (e.target.matches('[data-m-cadd-agree]')) this.syncCheckout();
      }.bind(this));

      if (this.dialog) {
        // With showModal() a click on the backdrop reports the dialog itself
        // as the target, since the backdrop is its pseudo-element.
        this.dialog.addEventListener('click', function (e) {
          if (e.target === this.dialog) this.close();
        }.bind(this));

        // Escape leaves through the same animation as the other ways out.
        this.dialog.addEventListener('cancel', function (e) {
          e.preventDefault();
          this.close();
        }.bind(this));

        this.dialog.addEventListener('close', function () {
          // Chrome refuses a second cancelled Escape and closes outright, so
          // the closing state is cleared here as well as after the animation.
          this.dialog.classList.remove('is-closing');
          if (this.activeElement && document.contains(this.activeElement)) {
            quietFocus(this.activeElement);
          }
        }.bind(this));
      }
    }

    /* ------------------------------------------------------------------
       Interface product-form.js calls
       ------------------------------------------------------------------ */

    getSectionsToRender() {
      return [{ id: 'minery-cart-body' }, { id: 'cart-icon-bubble' }];
    }

    setActiveElement(element) {
      this.activeElement = element;
    }

    renderContents(parsedState) {
      var sections = (parsedState && parsedState.sections) || {};

      // Dawn's own section, so it arrives wrapped in .shopify-section.
      var bubbleSource = nodeFrom(sections['cart-icon-bubble'], '.shopify-section');
      var bubbleTarget = document.getElementById('cart-icon-bubble');
      if (bubbleSource && bubbleTarget) bubbleTarget.innerHTML = bubbleSource.innerHTML;

      var fresh = nodeFrom(sections['minery-cart-body'], '[data-m-cadd-inner]');
      if (fresh && this.body) this.body.innerHTML = fresh.outerHTML;

      this.showLine(parsedState && parsedState.key);
      this.syncCheckout();
      this.loadRecs(parsedState && parsedState.product_id);

      // minery-fly.js may still have the product in the air on its way to
      // the cart icon; the dialog's backdrop would cover it, so open once it
      // has landed.
      var self = this;
      var landed = window.mFly && window.mFly.landed;
      if (landed && typeof landed.then === 'function') {
        landed.then(function () { self.open(); });
      } else {
        this.open();
      }
    }

    /* ------------------------------------------------------------------
       Dialog
       ------------------------------------------------------------------ */

    open() {
      if (!this.dialog || this.dialog.open) return;

      // The shop page's quick view is itself a <dialog>. Adding from inside it
      // would leave two modals stacked in the top layer, so close whatever is
      // already open first.
      var self = this;
      Array.prototype.forEach.call(document.querySelectorAll('dialog[open]'), function (d) {
        if (d !== self.dialog) d.close();
      });

      // showModal gives focus containment, Escape and the backdrop for free.
      this.dialog.classList.remove('is-closing');
      this.dialog.showModal();
      quietOpenFocus(this.dialog);
    }

    // Leaving is quicker than arriving (200ms, 260ms as a sheet). The out
    // animation is keyed on the dialog itself; the tick's own animations
    // bubble here too and are ignored.
    close() {
      var dialog = this.dialog;
      if (!dialog || !dialog.open || dialog.classList.contains('is-closing')) return;

      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduce) {
        dialog.close();
        return;
      }

      var done = false;
      function end() {
        if (done) return;
        done = true;
        dialog.removeEventListener('animationend', onEnd);
        dialog.close();
      }
      function onEnd(e) {
        if (e.target === dialog) end();
      }

      dialog.addEventListener('animationend', onEnd);
      dialog.classList.add('is-closing');
      window.setTimeout(end, 400);
    }

    /* The body carries one block per cart line; show the one that was just
       added. Adding a product already in the cart merges into the existing
       line and keeps its key, so this still resolves. */
    showLine(key) {
      if (!this.body) return;

      var lines = Array.prototype.slice.call(
        this.body.querySelectorAll('[data-m-cadd-line]')
      );
      if (lines.length === 0) return;

      var matched = false;
      lines.forEach(function (el) {
        var hit = el.getAttribute('data-m-cadd-line') === String(key);
        el.hidden = !hit;
        if (hit) matched = true;
      });

      // Never open with an empty product column.
      if (!matched) lines[lines.length - 1].hidden = false;
    }

    syncCheckout() {
      if (!this.body) return;
      var agree = this.body.querySelector('[data-m-cadd-agree]');
      var checkout = this.body.querySelector('[data-m-cadd-checkout]');
      if (!agree || !checkout) return;
      checkout.disabled = !agree.checked;
    }

    /* ------------------------------------------------------------------
       "With this product also buy"

       A second request, so the dialog is never held up waiting for it, and a
       failure leaves the confirmation itself untouched. The slot only exists
       when the section has recommendations switched on.
       ------------------------------------------------------------------ */

    loadRecs(productId) {
      if (!this.body || !productId) return;

      var slot = this.body.querySelector('[data-m-cadd-recslot]');
      var wrap = this.body.querySelector('[data-m-cadd-recs]');
      if (!slot || !wrap) return;

      var base = this.getAttribute('data-recs-url');
      if (!base) return;

      var limit = this.getAttribute('data-recs-count') || '4';
      var url =
        base +
        '?section_id=minery-cart-recs&limit=' +
        encodeURIComponent(limit) +
        '&product_id=' +
        encodeURIComponent(productId);

      fetch(url)
        .then(function (r) {
          if (!r.ok) throw new Error(String(r.status));
          return r.text();
        })
        .then(function (html) {
          var grid = nodeFrom(html, '[data-m-cadd-recgrid]');
          if (!grid) return; // nothing recommended for this product
          slot.innerHTML = '';
          slot.appendChild(grid);
          wrap.hidden = false;
          this.recsFor = productId;
        }.bind(this))
        .catch(function () {
          /* upsell is optional — leave the block hidden */
        });
    }
  }

  customElements.define('cart-notification', MineryCartPopup);
})();
